import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

gsap.registerPlugin(ScrollTrigger);

export { gsap, ScrollTrigger };

/* ------------------------------------------------------------------ *
 * Reduced motion
 * ------------------------------------------------------------------ */

const reduceQuery =
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)")
    : null;

export const prefersReducedMotion = () => Boolean(reduceQuery && reduceQuery.matches);

/* Reveals start hidden only once this module has actually run. If the bundle
   fails to execute, the class is never set and every section stays readable. */
if (typeof document !== "undefined" && !prefersReducedMotion()) {
  document.documentElement.classList.add("motion-ready");
}

/* ------------------------------------------------------------------ *
 * Smooth scrolling
 * ------------------------------------------------------------------ */

let lenis = null;
let mounted = 0;

export const getLenis = () => lenis;

const tick = (time) => {
  if (lenis) lenis.raf(time * 1000);
};

/**
 * Starts Lenis once, however many routes ask for it, and tears it down when
 * the last one unmounts. Wheel and keyboard only: touch devices keep their
 * native scrolling, which is smoother than anything JavaScript can fake and
 * does not fight the browser's address-bar behaviour.
 */
export const startSmoothScroll = () => {
  mounted += 1;
  if (lenis || prefersReducedMotion()) return;

  lenis = new Lenis({
    duration: 1.05,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    syncTouch: false,
    touchMultiplier: 1.6,
  });

  lenis.on("scroll", ScrollTrigger.update);
  gsap.ticker.add(tick);
  gsap.ticker.lagSmoothing(0);

  /* Exposed so anything driving the page from outside React — a test harness,
     a screenshot run — can move the real scroller instead of fighting it. */
  window.__lenis = lenis;
};

export const stopSmoothScroll = () => {
  mounted = Math.max(0, mounted - 1);
  if (mounted > 0 || !lenis) return;
  gsap.ticker.remove(tick);
  lenis.destroy();
  lenis = null;
  delete window.__lenis;
};

/**
 * Drawers, lightboxes and modals need the page behind them to hold still.
 * Body overflow alone is not enough once Lenis is driving the scroll.
 */
export const setScrollLocked = (locked) => {
  if (!lenis) return;
  if (locked) lenis.stop();
  else lenis.start();
};

const navbarOffset = () => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    "--navbar-height"
  );
  return -(parseInt(raw, 10) || 76) - 8;
};

/** One scroll path for nav links, hero buttons, footer links and /#hash arrivals. */
export const scrollToSection = (id) => {
  const target = typeof id === "string" ? document.getElementById(id) : id;
  if (!target) return;

  /* Resolve the destination here rather than handing Lenis an element: an
     absolute pixel target lands identically whichever scroller is driving. */
  const top = Math.max(
    0,
    target.getBoundingClientRect().top + window.scrollY + navbarOffset()
  );

  if (lenis) {
    lenis.scrollTo(top, { duration: 1.1 });
    return;
  }
  window.scrollTo({
    top,
    behavior: prefersReducedMotion() ? "auto" : "smooth",
  });
};

/* ------------------------------------------------------------------ *
 * Layout settling
 * ------------------------------------------------------------------ */

/* Photographs and webfonts land after the first measure, so every trigger
   position has to be recalculated once they do. */
export const refreshTriggers = () => ScrollTrigger.refresh();

if (typeof window !== "undefined") {
  window.addEventListener("load", () => ScrollTrigger.refresh());
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => ScrollTrigger.refresh()).catch(() => {});
  }
}

/* ------------------------------------------------------------------ *
 * Reusable scroll effects
 * ------------------------------------------------------------------ */

/**
 * Drifts an element against the scroll direction across its own span.
 * `strength` is the total travel in pixels, split either side of centre.
 */
export const parallax = (node, strength = 60) => {
  if (!node || prefersReducedMotion()) return () => {};
  const ctx = gsap.context(() => {
    gsap.fromTo(
      node,
      { y: strength / 2 },
      {
        y: -strength / 2,
        ease: "none",
        scrollTrigger: {
          trigger: node.parentElement || node,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      }
    );
  }, node);
  return () => ctx.revert();
};

/**
 * Wipes a heading in line by line. Wraps each line in its own overflow-hidden
 * box, so the type rises out of the rule beneath it rather than fading.
 */
export const wipeLines = (node, { delay = 0 } = {}) => {
  if (!node || prefersReducedMotion()) return () => {};

  const ctx = gsap.context(() => {
    gsap.from(node, {
      yPercent: 108,
      duration: 0.95,
      ease: "power4.out",
      delay,
      scrollTrigger: { trigger: node, start: "top 92%", once: true },
    });
  }, node);
  return () => ctx.revert();
};

/** Draws an SVG path on as it scrolls into view. */
export const drawPath = (path, { duration = 1.6, delay = 0 } = {}) => {
  if (!path) return () => {};
  const length = path.getTotalLength ? path.getTotalLength() : 0;
  if (!length) return () => {};

  if (prefersReducedMotion()) {
    gsap.set(path, { strokeDasharray: "none", strokeDashoffset: 0 });
    return () => {};
  }

  const ctx = gsap.context(() => {
    gsap.fromTo(
      path,
      { strokeDasharray: length, strokeDashoffset: length },
      {
        strokeDashoffset: 0,
        duration,
        delay,
        ease: "power2.inOut",
        scrollTrigger: { trigger: path, start: "top 95%", once: true },
      }
    );
  }, path);
  return () => ctx.revert();
};

/**
 * Draws the hairline in every section marker as that section arrives.
 * Registered once from the route rather than in six components, and safe to
 * call again after new markers mount.
 */
export const initMarkers = (root = document) => {
  if (prefersReducedMotion()) return () => {};

  const triggers = [];
  root.querySelectorAll(".marker:not([data-ruled])").forEach((marker) => {
    marker.dataset.ruled = "true";
    gsap.set(marker, { "--rule-scale": 0 });
    triggers.push(
      gsap.to(marker, {
        "--rule-scale": 1,
        duration: 1.1,
        ease: "power3.out",
        scrollTrigger: { trigger: marker, start: "top 92%", once: true },
      })
    );
  });

  return () => triggers.forEach((t) => t.scrollTrigger?.kill());
};
